import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Tabs, TabsContent, TabsList, TabsTrigger, Progress, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@pagespeedify/ui';
import { AlertTriangleIcon, CheckCircle2Icon, XCircleIcon } from 'lucide-react';

// --- INTERFACES ---
interface FilmstripImage {
    time: string;
    imageUrl: string;
}

interface BudgetDisplayData {
    key: string;
    label: string;
    size: number;
    budget: number;
    percentage: number;
    isOverBudget: boolean;
    indicatorClassName: string;
    textColorClassName: string;
}

interface VisualisationsProps {
    filmstrip: FilmstripImage[];
    budgetDisplayData: BudgetDisplayData[];
    resourceSummaryItems: any[]; // For pie chart
    pieChartDataset: any; // For pie chart
    PerformanceChartComponent: React.ElementType; // Pass the chart component itself
}

// --- COMPONENT ---
const Visualisations: React.FC<VisualisationsProps> = ({ 
    filmstrip, 
    budgetDisplayData,
    resourceSummaryItems,
    pieChartDataset,
    PerformanceChartComponent
}) => {
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

    const handleMouseEnter = (imageUrl: string) => {
        if (hoverTimeout) {
            clearTimeout(hoverTimeout);
        }
        const timeout = setTimeout(() => {
            setSelectedImage(imageUrl);
        }, 300); // 300ms delay
        setHoverTimeout(timeout);
    };

    const handleMouseLeave = () => {
        if (hoverTimeout) {
            clearTimeout(hoverTimeout);
            setHoverTimeout(null);
        }
    };
    
    const handleCloseModal = () => {
        setSelectedImage(null);
    };

    useEffect(() => {
        const handleScroll = () => {
            if (selectedImage) {
                handleCloseModal();
            }
        };

        if (selectedImage) {
            window.addEventListener('scroll', handleScroll, true);
        }

        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [selectedImage]);

    return (
        <Card id="visualisations" className="mb-8 border-0 shadow-none">
            <CardHeader className="border-b-0">
                <CardTitle className="text-2xl font-semibold text-card-foreground">Visualisations</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
                <Tabs defaultValue="resources-budget" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="resources-budget">Resources & Budget</TabsTrigger>
                        <TabsTrigger value="filmstrip">Filmstrip</TabsTrigger>
                    </TabsList>

                    {/* --- Resources & Budget Tab --- */}
                    <TabsContent value="resources-budget" className="mt-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                            {/* Left Column: Pie Chart */}
                            <div className="h-96 relative">
                                {pieChartDataset && PerformanceChartComponent ? (
                                    <PerformanceChartComponent
                                        type='pie'
                                        title="Resource Transfer Size Distribution"
                                        data={{
                                            labels: resourceSummaryItems.map((item: any) => item.label),
                                            datasets: [pieChartDataset]
                                        }}
                                        options={{
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: {
                                                    position: 'right',
                                                    labels: {
                                                        color: 'hsl(var(--foreground))',
                                                        boxWidth: 20,
                                                        padding: 20,
                                                    }
                                                },
                                                tooltip: {
                                                    backgroundColor: 'hsl(var(--background))',
                                                    titleColor: 'hsl(var(--foreground))',
                                                    bodyColor: 'hsl(var(--muted-foreground))',
                                                    borderColor: 'hsl(var(--border))',
                                                    borderWidth: 1,
                                                    callbacks: {
                                                        label: function(context: any) {
                                                            let label = context.label || '';
                                                            if (label) {
                                                                label += ': ';
                                                            }
                                                            if (context.parsed !== null) {
                                                                label += context.parsed.toFixed(2) + ' KB';
                                                            }
                                                            return label;
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-full">
                                        <p className="text-muted-foreground">Loading chart...</p>
                                    </div>
                                )}
                            </div>

                            {/* Right Column: Performance Budget */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-card-foreground">Performance Budget</h3>
                                <p className="text-sm text-muted-foreground">
                                    Compare the page’s resource sizes against recommended performance budgets.
                                </p>
                                <div className="flex items-center gap-4 mb-2 mt-1">
                                    <div className="flex items-center gap-1"><XCircleIcon className="w-4 h-4 text-report-fail" /><span className="text-xs text-report-fail font-medium">Poor (&gt;100%)</span></div>
                                    <div className="flex items-center gap-1"><AlertTriangleIcon className="w-4 h-4 text-report-improvement" /><span className="text-xs text-report-improvement font-medium">Needs Improvement (80-100%)</span></div>
                                    <div className="flex items-center gap-1"><CheckCircle2Icon className="w-4 h-4 text-report-good" /><span className="text-xs text-report-good font-medium">Good (&lt;80%)</span></div>
                                </div>
                                <div className="space-y-3 pt-2">
                                    {budgetDisplayData.map((item) => item && (
                                        <TooltipProvider key={`budget-item-${item.key}`}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div className="w-full">
                                                        <div className="flex justify-between text-sm font-medium mb-1">
                                                            <span className="text-muted-foreground">{item.label}</span>
                                                            <span className={item.textColorClassName}>
                                                                {item.size.toFixed(1)} KB / {item.budget} KB
                                                            </span>
                                                        </div>
                                                        <Progress
                                                            value={item.percentage}
                                                            className={item.indicatorClassName}
                                                        />
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>{item.isOverBudget ? `Over budget by ${(item.size - item.budget).toFixed(1)} KB` : `Within budget`}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    {/* --- Filmstrip Tab --- */}
                    <TabsContent value="filmstrip" className="mt-6">
                        {filmstrip && filmstrip.length > 0 ? (
                            <div className="flex overflow-x-auto space-x-4 pb-4">
                                {filmstrip.map((frame, index) => (
                                    <div 
                                        key={`filmstrip-frame-${index}-${frame.time}`}
                                        className="flex-shrink-0 text-center group"
                                        onMouseEnter={() => handleMouseEnter(frame.imageUrl)}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <div 
                                            className="border-2 border-border rounded-lg w-40 bg-muted flex items-center justify-center overflow-hidden group-hover:opacity-80 transition-opacity"
                                            style={{ height: '220px' }}
                                        >
                                            <img 
                                                src={frame.imageUrl} 
                                                alt={`Page load at ${frame.time}`}
                                                className="max-w-full max-h-full object-contain"
                                                loading="lazy"
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        </div>
                                        <p className="mt-2 text-sm font-medium text-muted-foreground">{frame.time}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted-foreground text-center py-8">Filmstrip data is not available for this report.</p>
                        )}
                    </TabsContent>
                </Tabs>
            </CardContent>
            {selectedImage && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
                    onClick={handleCloseModal}
                >
                    <button 
                        className="absolute top-4 right-4 text-white z-50 p-2 rounded-full bg-black/50 hover:bg-black/75 transition-colors"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCloseModal();
                        }}
                        aria-label="Close enlarged image"
                    >
                        <XCircleIcon className="w-8 h-8" />
                    </button>
                    <div className="relative p-4">
                        <img 
                            src={selectedImage} 
                            alt="Enlarged filmstrip view" 
                            className="max-w-[90vw] max-h-[90vh] object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}
        </Card>
    );
};

export default Visualisations;
